'use client'

import OutletIcon from '@/components/ui/OutletIcon'
import { trackOutboundArticle } from '@/lib/tracking'
import { DEFAULT_ORDER, rankArticles } from '@/utils/articleOrder'
import { getBiasCategory } from '@/utils/bias'
import { getReliabilityLabel } from '@/utils/outlets'
import { useMemo, useState } from 'react'
import { quotedOutlets } from './StoryFormat'
import { useTopicContext } from './TopicContext'

// "Read the coverage" (the concept, 2026-11-01): the outlets' own words, a
// column per lean, ONE headline per outlet so a busy outlet can't fill a
// column. Each column is headed by its article count in its side's colour,
// "LEFT-LEANING" and how many outlets; each entry is the outlet's icon with
// its lean ring, its name and reliability, the headline, and "+ 5 more from
// CNN" under it. No "Quoted above" tag and no note on the order under the
// columns (Sam, 2026-11-01: simpler); no Analysis / Opinion tags and no
// news-only menu either (Sam, 2026-11-03: too busy).
//
// Order in a column: outlets the prose quotes first, then by how many
// articles they ran, ties to the more reliable outlet. An outlet's headline
// is the one the prose cited from it, else the panel's first (utils/
// articleOrder). On a phone the sides are a pill switch and one column shows
// at a time.

const PER_COLUMN = 5

const LEANS = [
  {
    key: 'left',
    name: 'Left-leaning',
    word: 'left-leaning',
    text: 'text-blue-600 dark:text-blue-400',
    rule: 'md:border-blue-600',
    fill: 'bg-blue-600 text-white',
  },
  {
    key: 'center',
    name: 'Center',
    word: 'center',
    text: 'text-muted-foreground',
    rule: 'md:border-zinc-500',
    fill: 'bg-zinc-600 text-white',
  },
  {
    key: 'right',
    name: 'Right-leaning',
    word: 'right-leaning',
    text: 'text-red-600 dark:text-red-400',
    rule: 'md:border-red-600',
    fill: 'bg-red-600 text-white',
  },
]

const sideOf = (a) =>
  typeof a.outlet?.bias === 'number' ? getBiasCategory(a.outlet.bias) : 'center'

// { left: { articles, outlets: [{ outlet, headline, count, quoted }] }, … }
function columns(articles, quoted, cited) {
  const cols = {
    left: { articles: [], by: new Map() },
    center: { articles: [], by: new Map() },
    right: { articles: [], by: new Map() },
  }
  for (const a of articles) {
    if (!a.url || !a.title || !a.outlet) continue
    const col = cols[sideOf(a)]
    col.articles.push(a)
    const key = a.outlet.slug || a.outlet.name
    const o = col.by.get(key) || {
      key,
      outlet: a.outlet,
      list: [],
      quoted: quoted.has(key),
    }
    o.list.push(a)
    col.by.set(key, o)
  }
  const out = {}
  for (const [side, col] of Object.entries(cols)) {
    const outlets = [...col.by.values()].map((o) => {
      const ranked = rankArticles(o.list, DEFAULT_ORDER)
      return {
        ...o,
        count: o.list.length,
        headline: o.list.find((a) => cited.has(a.id)) || ranked[0],
      }
    })
    outlets.sort(
      (a, b) =>
        Number(b.quoted) - Number(a.quoted) ||
        b.count - a.count ||
        (b.outlet.reliability ?? 0) - (a.outlet.reliability ?? 0)
    )
    out[side] = {
      articles: rankArticles(col.articles, DEFAULT_ORDER),
      outlets,
    }
  }
  return out
}

function Entry({ entry, topicSlug }) {
  const { outlet, headline, count } = entry
  return (
    <a
      href={headline.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        trackOutboundArticle(headline, {
          story_slug: topicSlug,
          surface: 'headlines_list',
        })
      }
      className="group block border-b border-border py-3"
    >
      <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1 font-sans text-xs font-semibold text-foreground">
        <OutletIcon outlet={outlet} className="mr-1" />
        {outlet.name}
        {typeof outlet.reliability === 'number' && (
          <span className="font-normal text-muted-foreground">
            {getReliabilityLabel(outlet.reliability)}
          </span>
        )}
      </span>
      <span className="mt-1.5 block font-serif text-[17px] leading-[1.35] text-foreground group-hover:underline">
        {headline.title}{' '}
        <span aria-hidden="true" className="text-muted-foreground">
          ↗
        </span>
      </span>
      {count > 1 && (
        <span className="mt-1 block font-sans text-xs text-muted-foreground">
          + {count - 1} more from {outlet.name}
        </span>
      )}
    </a>
  )
}

export default function SideHeadlines({
  articles = [],
  storyFormat,
  topicSlug,
}) {
  const { openArticlesModal } = useTopicContext()
  const [tab, setTab] = useState('left')

  const quoted = useMemo(
    () => quotedOutlets(storyFormat, articles),
    [storyFormat, articles]
  )
  const cited = useMemo(
    () => new Set((storyFormat?.cited_article_ids || []).map(Number)),
    [storyFormat]
  )
  const cols = useMemo(
    () => columns(articles, quoted, cited),
    [articles, quoted, cited]
  )

  // Whether the story has two sides to read at all.
  const sides = useMemo(
    () =>
      new Set(articles.filter((a) => a.url && a.title && a.outlet).map(sideOf))
        .size,
    [articles]
  )
  const present = LEANS.filter(({ key }) => cols[key].outlets.length)
  if (sides < 2) return null
  const outletCount = present.reduce(
    (n, { key }) => n + cols[key].outlets.length,
    0
  )
  const current = present.some((p) => p.key === tab) ? tab : present[0]?.key

  return (
    <section className="mt-[30px] md:mt-14">
      <div className="mb-4 border-b-[3px] border-foreground pb-3 md:mb-[22px]">
        <div>
          <h2 className="font-serif text-2xl font-medium tracking-tight text-foreground md:text-[28px]">
            Read the coverage
          </h2>
          <p className="mt-1 font-sans text-[13px] text-muted-foreground">
            {articles.length} articles from {outletCount} outlets. Open any
            headline to read it on the outlet&rsquo;s site.
          </p>
        </div>
      </div>

      {/* The phone's pill switch, one option per side present. */}
      <div
        role="tablist"
        aria-label="Choose a side"
        className="mb-2 grid gap-[3px] rounded-full border border-foreground bg-card p-[3px] md:hidden"
        style={{
          gridTemplateColumns: `repeat(${present.length}, minmax(0, 1fr))`,
        }}
      >
        {present.map(({ key, name, fill }) => (
          <button
            key={key}
            role="tab"
            type="button"
            aria-selected={current === key}
            onClick={() => setTab(key)}
            className={`min-h-10 rounded-full font-sans text-[13px] font-semibold transition-colors ${
              current === key ? fill : 'text-foreground hover:bg-muted'
            }`}
          >
            {name.replace('-leaning', '')} · {cols[key].articles.length}
          </button>
        ))}
      </div>

      {/* Column count follows what's there; both class strings are written
          out because Tailwind only ships class names it can see. */}
      <div
        className={`md:grid md:gap-9 ${
          present.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'
        }`}
      >
        {present.map(({ key, name, word, text, rule }) => {
          const col = cols[key]
          return (
            <div
              key={key}
              className={`${current === key ? 'block' : 'hidden'} min-w-0 md:block`}
            >
              <div
                className={`hidden items-center gap-3 border-b border-foreground py-2.5 md:flex md:border-t-4 ${rule}`}
              >
                <span
                  className={`font-serif text-[34px] leading-none tabular-nums ${text}`}
                >
                  {col.articles.length}
                </span>
                <span>
                  <span
                    className={`block font-sans text-xs font-semibold uppercase tracking-[0.12em] ${text}`}
                  >
                    {name}
                  </span>
                  <span className="font-sans text-xs text-muted-foreground">
                    {col.outlets.length}{' '}
                    {col.outlets.length === 1 ? 'outlet' : 'outlets'}
                  </span>
                </span>
              </div>
              <ul>
                {col.outlets.slice(0, PER_COLUMN).map((o) => (
                  <li key={o.key}>
                    <Entry entry={o} topicSlug={topicSlug} />
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() =>
                  openArticlesModal(col.articles, `${name} coverage`)
                }
                className="mt-3 font-sans text-[13px] font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2"
              >
                {col.outlets.length > PER_COLUMN
                  ? `All ${col.outlets.length} ${word} outlets`
                  : `All ${col.articles.length} ${word} articles`}
              </button>
            </div>
          )
        })}
      </div>
    </section>
  )
}
